import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Building2, Save, Loader2, MapPin, Phone, Eye, CheckCircle, Image, Trash2, Upload, Mail, Plus, X, BadgeCheck, Clock, AlertCircle, BarChart3, ChevronLeft, ExternalLink, Crown, Sparkles, Video, Shield, Star, CreditCard, Users, DollarSign, UserCheck, Target, Globe, Pencil, GitBranch, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { ProfileAnalyticsDashboard } from '../components/AnalyticsDashboard';
import { AIDescriptionButton } from '../components/AIDescriptionButton';
import { CHILEAN_REGIONS, getCitiesByRegion } from '../constants/chileanLocations';
import CenterEditor from '../components/admin/CenterEditor';
import BranchManager from '../components/center/BranchManager';
import QuoteRequestsInbox from '../components/QuoteRequestsInbox';
import { API } from '../utils/api';


// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Axios instance with auth
const authAxios = {
  get: (url, config = {}) => axios.get(url, { 
    ...config, 
    withCredentials: true,
    headers: { ...getAuthHeaders(), ...config.headers }
  }),
  post: (url, data, config = {}) => axios.post(url, data, { 
    ...config, 
    withCredentials: true,
    headers: { ...getAuthHeaders(), ...config.headers }
  }),
  put: (url, data, config = {}) => axios.put(url, data, { 
    ...config, 
    withCredentials: true,
    headers: { ...getAuthHeaders(), ...config.headers }
  }),
  delete: (url, config = {}) => axios.delete(url, { 
    ...config, 
    withCredentials: true,
    headers: { ...getAuthHeaders(), ...config.headers }
  })
};

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt', 'Viña del Mar', 'Iquique', 'Calama', 'Copiapó', 'Coquimbo', 'Chillán', 'Osorno', 'Valdivia', 'Punta Arenas'];

// Same options as admin CenterEditor for consistency
const serviceOptions = [
  'Desintoxicación',
  'Terapia individual',
  'Terapia grupal',
  'Terapia familiar',
  'Psiquiatría',
  'Psicología',
  'Trabajo social',
  'Nutrición',
  'Actividades recreativas',
  'Talleres ocupacionales',
  'Seguimiento post-tratamiento',
  'Intervención en crisis',
  'Programa 12 pasos'
];

const addictionOptions = [
  'Alcohol',
  'Drogas',
  'Cocaína',
  'Marihuana',
  'Opioides',
  'Medicamentos',
  'Juego patológico',
  'Adicciones comportamentales',
  'Tabaco',
  'Patología dual'
];

const modalityOptions = [
  { value: 'Residencial', label: 'Residencial' },
  { value: 'Ambulatorio', label: 'Ambulatorio' },
  { value: 'Online', label: 'Online' },
  { value: 'Mixto', label: 'Mixto' },
  { value: 'Hospital de día', label: 'Hospital de día' },
  { value: 'Internación parcial', label: 'Internación parcial' }
];

const genderOptions = [
  { value: 'hombre', label: 'Solo Hombres' },
  { value: 'mujer', label: 'Solo Mujeres' },
  { value: 'mixto', label: 'Mixto' }
];

const workshopOptions = ['Teatro', 'Meditación', 'Habilidades sociales', 'Prevención de recaída', 'Deportes', 'Arte', 'Música', 'Yoga'];

// Preview Component - Shows how the center will look in the public directory
function CenterPreview({ data }) {
  if (!data || !data.name) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="py-12 text-center">
          <Eye className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">Completa los datos para ver la vista previa</p>
        </CardContent>
      </Card>
    );
  }

  var centerImage = data.logo || (data.gallery && data.gallery[0]);
  var modalityList = Array.isArray(data.modality) ? data.modality : (data.modality ? [data.modality] : []);
  var addictionsList = data.addictions_treated || data.specializations || [];

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-slate-50 flex items-center gap-2">
          <Eye className="w-5 h-5 text-emerald-400" />
          Vista Previa del Perfil
        </CardTitle>
        <p className="text-slate-400 text-sm">Así se verá tu centro en el directorio público</p>
      </CardHeader>
      <CardContent>
        {/* Preview Card - Similar to public directory */}
        <div className="bg-white rounded-lg overflow-hidden shadow-lg max-w-md mx-auto">
          {/* Image */}
          <div className="aspect-video bg-slate-100 relative overflow-hidden">
            {centerImage ? (
              <img src={centerImage} alt={data.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Building2 className="w-16 h-16 text-slate-300" />
              </div>
            )}
            {/* Badges */}
            <div className="absolute top-3 left-3 flex gap-2">
              {data.is_premium && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
              {data.is_verified && (
                <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                  <BadgeCheck className="w-3 h-3" /> Verificado
                </span>
              )}
            </div>
            {/* Modality Badge */}
            {modalityList.length > 0 && (
              <div className="absolute bottom-3 left-3">
                <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-md">
                  {modalityList[0]}
                </span>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="p-5">
            {/* Location */}
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
              <MapPin className="w-4 h-4 text-emerald-500" />
              <span>{data.city || 'Chile'}</span>
            </div>
            
            {/* Name */}
            <h3 className="font-semibold text-lg text-slate-800 mb-2 line-clamp-2">
              {data.name}
            </h3>
            
            {/* Modality badges */}
            {modalityList.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {modalityList.map(function(mod, idx) {
                  return (
                    <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {mod}
                    </span>
                  );
                })}
              </div>
            )}
            
            {/* Addictions */}
            {addictionsList.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {addictionsList.slice(0, 4).map(function(spec, idx) {
                  var specColors = {
                    'Alcohol': 'bg-amber-100 text-amber-700',
                    'Drogas': 'bg-red-100 text-red-700',
                    'Cocaína': 'bg-pink-100 text-pink-700',
                    'Marihuana': 'bg-green-100 text-green-700'
                  };
                  return (
                    <span key={idx} className={'text-xs px-2 py-0.5 rounded-full ' + (specColors[spec] || 'bg-slate-100 text-slate-600')}>
                      {spec}
                    </span>
                  );
                })}
              </div>
            )}
            
            {/* Gender & Price */}
            <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
              {data.gender && (
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {data.gender === 'mixto' ? 'Mixto' : data.gender === 'hombre' ? 'Hombres' : 'Mujeres'}
                </span>
              )}
              {(data.price_min || data.price_max) && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {data.price_min && data.price_max 
                    ? '$' + Number(data.price_min).toLocaleString('es-CL') + ' - $' + Number(data.price_max).toLocaleString('es-CL')
                    : data.price_min 
                      ? 'Desde $' + Number(data.price_min).toLocaleString('es-CL')
                      : 'Hasta $' + Number(data.price_max).toLocaleString('es-CL')
                  }
                </span>
              )}
            </div>
            
            {/* Schedule */}
            {data.schedule && (
              <div className="flex items-center gap-2 text-sm text-slate-500 border-t border-slate-100 pt-3">
                <Clock className="w-4 h-4" />
                <span>{data.schedule}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Form for creating/editing a center
function CenterForm({ initialData, onSubmit, onCancel, loading }) {
  const auth = useAuth();
  const [formData, setFormData] = useState(initialData || {
    name: '', slug: '', description: '', address: '', city: '', region: '', phone: '', whatsapp: '', email: auth.user?.email || '',
    website: '', youtube_video: '', services: [], specializations: [], addictions_treated: [], modality: [], gender: '',
    price_min: '', price_max: '', treatment_duration_min: '', treatment_duration_max: '',
    workshops: [], custom_workshops: '', logo: '', gallery: [], is_public: true,
    // New fields
    schedule: '', facebook: '', instagram: '', capacity: '', min_age: '', max_age: ''
  });
  const [uploading, setUploading] = useState(false);
  const galleryInputRef = useRef(null);

  function handleChange(e) {
    setFormData(function(prev) { return Object.assign({}, prev, { [e.target.name]: e.target.value }); });
  }

  // Auto-generate slug from name and city
  function handleNameChange(e) {
    var newName = e.target.value;
    var newSlug = newName.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    if (formData.city) {
      var citySlug = formData.city.toLowerCase().split(' ')[0];
      newSlug = newSlug + '-' + citySlug;
    }
    setFormData(function(prev) { return Object.assign({}, prev, { name: newName, slug: newSlug }); });
  }

  function handleCityChange(cityValue) {
    var citySlug = cityValue.toLowerCase().split(' ')[0];
    var nameSlug = (formData.name || '').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    var newSlug = nameSlug ? nameSlug + '-' + citySlug : '';
    setFormData(function(prev) { return Object.assign({}, prev, { city: cityValue, slug: newSlug }); });
  }

  function handleMultiSelect(field, value) {
    setFormData(function(prev) {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return Object.assign({}, prev, { [field]: current.filter(function(v) { return v !== value; }) });
      }
      return Object.assign({}, prev, { [field]: current.concat([value]) });
    });
  }

  async function handleImageUpload(e, type) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Client-side validation
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('La imagen es muy grande. Máximo 10MB');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await authAxios.post(API + '/upload/image', formDataUpload, { withCredentials: true });
      const imageUrl = res.data.url;
      if (type === 'logo') {
        setFormData(function(prev) { return Object.assign({}, prev, { logo: imageUrl }); });
      } else {
        setFormData(function(prev) {
          const gallery = prev.gallery || [];
          return Object.assign({}, prev, { gallery: gallery.concat([imageUrl]) });
        });
      }
      toast.success('Imagen subida correctamente');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al subir imagen. Intente con otra imagen.';
      toast.error(errorMsg);
      console.error('Upload error:', err);
    }
    setUploading(false);
  }

  function removeGalleryImage(index) {
    setFormData(function(prev) {
      const gallery = (prev.gallery || []).filter(function(_, i) { return i !== index; });
      return Object.assign({}, prev, { gallery: gallery });
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    var dataToSend = Object.assign({}, formData, {
      price_min: formData.price_min ? parseInt(formData.price_min) : null,
      price_max: formData.price_max ? parseInt(formData.price_max) : null,
      treatment_duration_min: formData.treatment_duration_min ? parseInt(formData.treatment_duration_min) : null,
      treatment_duration_max: formData.treatment_duration_max ? parseInt(formData.treatment_duration_max) : null,
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
      min_age: formData.min_age ? parseInt(formData.min_age) : null,
      max_age: formData.max_age ? parseInt(formData.max_age) : null
    });
    onSubmit(dataToSend);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Building2 className="w-5 h-5 text-emerald-400" />Información básica</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">Nombre del centro *</Label>
            <Input name="name" value={formData.name} onChange={handleNameChange} required className="bg-slate-950 border-slate-700 text-slate-200" />
          </div>
          {/* Slug field - auto-generated from name */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <Label className="text-blue-400">URL del centro (se genera automáticamente)</Label>
            <Input 
              name="slug"
              value={formData.slug || ''} 
              onChange={function(e) { 
                var value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
                setFormData(function(prev) { return Object.assign({}, prev, { slug: value }); }); 
              }} 
              placeholder="se-genera-del-nombre"
              className="mt-1 bg-slate-800 border-blue-500/30 font-mono text-sm"
            />
            <p className="text-xs text-slate-400 mt-1">
              URL: <span className="text-emerald-400 font-mono">/centros/{formData.slug || '...'}</span>
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-300">Descripción</Label>
              <AIDescriptionButton
                entityType="center"
                name={formData.name}
                context={{
                  ciudad: formData.city,
                  servicios: formData.services?.join(', '),
                  tipoTratamiento: formData.treatment_type,
                  género: formData.gender
                }}
                onGenerated={function(desc) { setFormData(function(prev) { return Object.assign({}, prev, { description: desc }); }); }}
              />
            </div>
            <Textarea name="description" value={formData.description || ''} onChange={handleChange} rows={4} className="bg-slate-950 border-slate-700 text-slate-200" placeholder="Describe tu centro, metodología, enfoque de tratamiento..." />
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><MapPin className="w-5 h-5 text-emerald-400" />Ubicación</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">Dirección *</Label>
            <Input name="address" value={formData.address} onChange={handleChange} required className="bg-slate-950 border-slate-700 text-slate-200" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Región *</Label>
              <Select value={formData.region || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { region: v, city: '' }); }); }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar región" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {CHILEAN_REGIONS.map(function(r) { return <SelectItem key={r.value} value={r.value} className="text-slate-300 focus:bg-slate-800">{r.label}</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Ciudad *</Label>
              <Select value={formData.city || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { city: v }); }); }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar ciudad" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {(formData.region ? getCitiesByRegion(formData.region) : []).map(function(c) { return <SelectItem key={c} value={c} className="text-slate-300 focus:bg-slate-800">{c}</SelectItem>; })}
                  <SelectItem value="otra" className="text-slate-300 focus:bg-slate-800">Otra ciudad</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {formData.city === 'otra' && (
            <div>
              <Label className="text-slate-300">Especificar ciudad</Label>
              <Input name="city_other" value={formData.city_other || ''} onChange={handleChange} placeholder="Nombre de la ciudad" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Phone className="w-5 h-5 text-emerald-400" />Contacto</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Teléfono *</Label>
              <Input name="phone" value={formData.phone} onChange={handleChange} required className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
            <div>
              <Label className="text-slate-300">WhatsApp</Label>
              <Input name="whatsapp" value={formData.whatsapp || ''} onChange={handleChange} placeholder="+56 9 1234 5678" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
            <div>
              <Label className="text-slate-300">Sitio web</Label>
              <Input name="website" type="url" value={formData.website || ''} onChange={handleChange} placeholder="https://" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Video de YouTube</Label>
            <Input name="youtube_video" value={formData.youtube_video || ''} onChange={handleChange} placeholder="https://www.youtube.com/watch?v=..." className="bg-slate-950 border-slate-700 text-slate-200" />
          </div>
        </CardContent>
      </Card>

      {/* Treatment Details */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-slate-50">Detalles del Tratamiento</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-slate-300 mb-3 block">Modalidad de tratamiento</Label>
            <div className="flex flex-wrap gap-2">
              {modalityOptions.map(function(m) {
                return <Button key={m.value} type="button" variant={(formData.modality || []).includes(m.value) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('modality', m.value); }} className={(formData.modality || []).includes(m.value) ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{m.label}</Button>;
              })}
            </div>
          </div>
          <div>
            <Label className="text-slate-300 mb-3 block">Tipo de tratamiento (Enfoque terapéutico)</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'cognitivo-conductual', label: 'Cognitivo-Conductual' },
                { value: 'cognitivo', label: 'Cognitivo' },
                { value: 'conductual', label: 'Conductual' },
                { value: 'humanista', label: 'Humanista' },
                { value: 'holistico', label: 'Holístico' },
                { value: 'integrativo', label: 'Integrativo' },
                { value: '12-pasos', label: '12 Pasos' },
                { value: 'sistemico', label: 'Sistémico/Familiar' }
              ].map(function(t) {
                return <Button key={t.value} type="button" variant={(formData.therapy_approaches || []).includes(t.value) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('therapy_approaches', t.value); }} className={(formData.therapy_approaches || []).includes(t.value) ? 'bg-purple-500 hover:bg-purple-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{t.label}</Button>;
              })}
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Género</Label>
            <Select value={formData.gender || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { gender: v }); }); }}>
              <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">{genderOptions.map(function(g) { return <SelectItem key={g.value} value={g.value} className="text-slate-300 focus:bg-slate-800">{g.label}</SelectItem>; })}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Precio desde (CLP)</Label>
              <Input name="price_min" type="number" value={formData.price_min || ''} onChange={handleChange} placeholder="500000" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
            <div>
              <Label className="text-slate-300">Precio hasta (CLP)</Label>
              <Input name="price_max" type="number" value={formData.price_max || ''} onChange={handleChange} placeholder="3000000" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Duración mínima (meses)</Label>
              <Input name="treatment_duration_min" type="number" value={formData.treatment_duration_min || ''} onChange={handleChange} placeholder="1" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
            <div>
              <Label className="text-slate-300">Duración máxima (meses)</Label>
              <Input name="treatment_duration_max" type="number" value={formData.treatment_duration_max || ''} onChange={handleChange} placeholder="6" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Specializations */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-slate-50">Adicciones y Servicios</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-slate-300 mb-3 block">Adicciones que tratan</Label>
            <div className="flex flex-wrap gap-2">
              {addictionOptions.map(function(a) {
                return <Button key={a} type="button" variant={(formData.addictions_treated || []).includes(a) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('addictions_treated', a); }} className={(formData.addictions_treated || []).includes(a) ? 'bg-red-500 hover:bg-red-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{a}</Button>;
              })}
            </div>
          </div>
          <div>
            <Label className="text-slate-300 mb-3 block">Servicios ofrecidos</Label>
            <div className="flex flex-wrap gap-2">
              {serviceOptions.map(function(s) {
                return <Button key={s} type="button" variant={(formData.services || []).includes(s) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('services', s); }} className={(formData.services || []).includes(s) ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{s}</Button>;
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedule & Social Media */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Clock className="w-5 h-5 text-emerald-400" />Horarios y Redes Sociales</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">Horario de atención</Label>
            <Input name="schedule" value={formData.schedule || ''} onChange={handleChange} placeholder="Lun-Vie 9:00-18:00, Sáb 9:00-13:00" className="bg-slate-950 border-slate-700 text-slate-200" />
            <p className="text-xs text-slate-500 mt-1">Describe tus horarios de atención al público</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Capacidad (personas)</Label>
              <Input name="capacity" type="number" value={formData.capacity || ''} onChange={handleChange} placeholder="20" className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300">Edad mínima</Label>
                <Input name="min_age" type="number" value={formData.min_age || ''} onChange={handleChange} placeholder="18" className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
              <div>
                <Label className="text-slate-300">Edad máxima</Label>
                <Input name="max_age" type="number" value={formData.max_age || ''} onChange={handleChange} placeholder="65" className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
            </div>
          </div>
          <div className="pt-4 border-t border-slate-700">
            <Label className="text-slate-300 mb-3 block">Redes Sociales</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 text-xs">Facebook</Label>
                <Input name="facebook" value={formData.facebook || ''} onChange={handleChange} placeholder="facebook.com/tucentro" className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Instagram</Label>
                <Input name="instagram" value={formData.instagram || ''} onChange={handleChange} placeholder="@tucentro" className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workshops */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-slate-50">Talleres</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300 mb-3 block">Talleres disponibles</Label>
            <div className="flex flex-wrap gap-2">
              {workshopOptions.map(function(w) {
                return <Button key={w} type="button" variant={(formData.workshops || []).includes(w) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('workshops', w); }} className={(formData.workshops || []).includes(w) ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{w}</Button>;
              })}
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Otros talleres (presiona Enter para agregar)</Label>
            {/* Custom workshops tags display */}
            {formData.custom_workshops && formData.custom_workshops.trim() && (
              <div className="flex flex-wrap gap-2 my-3">
                {formData.custom_workshops.split(',').filter(function(t) { return t.trim(); }).map(function(tag, idx) {
                  return (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500 text-emerald-400 rounded-lg text-sm">
                      {tag.trim()}
                      <button
                        type="button"
                        onClick={function() {
                          var tags = formData.custom_workshops.split(',').filter(function(t) { return t.trim(); });
                          tags.splice(idx, 1);
                          setFormData(function(prev) { return Object.assign({}, prev, { custom_workshops: tags.join(', ') }); });
                        }}
                        className="ml-1 hover:text-red-400"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <Input 
              placeholder="Escribe y presiona Enter..."
              className="bg-slate-950 border-slate-700 text-slate-200"
              onKeyDown={function(e) {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  var newTag = e.target.value.trim();
                  if (newTag) {
                    var currentTags = formData.custom_workshops ? formData.custom_workshops.split(',').filter(function(t) { return t.trim(); }) : [];
                    if (!currentTags.includes(newTag)) {
                      currentTags.push(newTag);
                      setFormData(function(prev) { return Object.assign({}, prev, { custom_workshops: currentTags.join(', ') }); });
                    }
                    e.target.value = '';
                  }
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Images */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-slate-50"><Image className="w-5 h-5 text-emerald-400" />Imágenes</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          {/* Logo */}
          <div>
            <Label className="text-slate-300 mb-3 block">Logo del centro</Label>
            <div className="flex items-center gap-4">
              {formData.logo ? (
                <div className="relative">
                  <img src={formData.logo} alt="Logo" className="w-24 h-24 rounded-xl object-contain bg-white p-1.5 border border-slate-700" />
                  <Button type="button" variant="destructive" size="icon" className="absolute -top-2 -right-2 w-6 h-6" onClick={function() { setFormData(function(prev) { return Object.assign({}, prev, { logo: '' }); }); }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-700 flex items-center justify-center bg-slate-950">
                  <Building2 className="w-8 h-8 text-slate-600" />
                </div>
              )}
              <div>
                <input type="file" accept="image/*" onChange={function(e) { handleImageUpload(e, 'logo'); }} className="hidden" id="logo-upload-form" />
                <label htmlFor="logo-upload-form">
                  <Button type="button" variant="outline" size="sm" asChild disabled={uploading} className="border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer">
                    <span>{uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</> : <><Upload className="w-4 h-4 mr-2" />Subir logo</>}</span>
                  </Button>
                </label>
                <p className="text-xs text-slate-500 mt-2">Recomendado: 200x200px</p>
              </div>
            </div>
          </div>

          {/* Gallery */}
          <div>
            <Label className="text-slate-300 mb-3 block">Galería de fotos</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {(formData.gallery || []).map(function(img, index) {
                return (
                  <div key={index} className="relative group">
                    <img src={img} alt={'Foto ' + (index + 1)} className="w-full h-24 object-cover rounded-lg border border-slate-700" />
                    <Button type="button" variant="destructive" size="icon" className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={function() { removeGalleryImage(index); }}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
              <div className="w-full h-24 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center bg-slate-950 cursor-pointer hover:border-emerald-500/50 transition-colors" onClick={function() { galleryInputRef.current?.click(); }}>
                <div className="text-center">
                  <Plus className="w-6 h-6 mx-auto text-slate-500" />
                  <span className="text-xs text-slate-500">Agregar foto</span>
                </div>
              </div>
              <input type="file" accept="image/*" onChange={function(e) { handleImageUpload(e, 'gallery'); }} className="hidden" ref={galleryInputRef} />
            </div>
            <p className="text-xs text-slate-500 mt-2">Sube fotos de las instalaciones, habitaciones, áreas comunes, etc.</p>
          </div>
        </CardContent>
      </Card>

      {/* Visibility */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-50">Mostrar en directorio</p>
              <p className="text-sm text-slate-400">Visible en búsquedas públicas</p>
            </div>
            <Switch checked={formData.is_public !== false} onCheckedChange={function(c) { setFormData(function(prev) { return Object.assign({}, prev, { is_public: c }); }); }} />
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <CenterPreview data={formData} />

      <div className="flex gap-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1 py-6 border-slate-700 text-slate-300 hover:bg-slate-800">
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1 py-6 bg-emerald-500 hover:bg-emerald-600 rounded-lg" disabled={loading}>
          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</> : <><Save className="w-5 h-5 mr-2" />Guardar Centro</>}
        </Button>
      </div>
    </form>
  );
}

export function CenterRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  async function handleSubmit(data) {
    setLoading(true);
    try {
      const res = await authAxios.post(API + '/center/register', data, { withCredentials: true });
      toast.success('Centro registrado');
      navigate('/center/dashboard?id=' + res.data.center_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-50 mb-2" data-testid="center-register-title">Registrar Centro</h1>
            <p className="text-slate-400">Completa la información de tu centro de rehabilitación</p>
          </div>
          <CenterForm onSubmit={handleSubmit} loading={loading} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function CenterDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('info');
  const [messages, setMessages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [requestingVerification, setRequestingVerification] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [showNewCenterDialog, setShowNewCenterDialog] = useState(false);
  const [creatingCenter, setCreatingCenter] = useState(false);
  const [view, setView] = useState('list'); // 'list' or 'detail'
  // Premium subscription states
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loadingPremium, setLoadingPremium] = useState(false);
  // Enhanced verification states
  const [verificationType, setVerificationType] = useState('videocall');
  const [verificationChecks, setVerificationChecks] = useState({
    has_dignified_treatment: true,
    has_therapy_programs: true,
    no_humiliation_policy: true
  });
  // Referral system states
  const [referralData, setReferralData] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [admissionModal, setAdmissionModal] = useState(null);
  const [admissionFee, setAdmissionFee] = useState('');
  const [submittingAdmission, setSubmittingAdmission] = useState(false);
  const [showCenterEditor, setShowCenterEditor] = useState(false);

  useEffect(function() {
    loadCenters();
    
    // Check URL params for premium subscription result
    const params = new URLSearchParams(window.location.search);
    if (params.get('premium_success') === '1') {
      toast.success('¡Plan Premium activado! Tu centro ahora aparece destacado');
      window.history.replaceState({}, '', window.location.pathname + '?id=' + params.get('center_id'));
    } else if (params.get('premium_cancelled') === '1') {
      toast.info('Suscripción cancelada');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(function() {
    const centerId = searchParams.get('id');
    if (centerId && centers.length > 0) {
      const center = centers.find(c => c.center_id === centerId);
      if (center) {
        setSelectedCenter(center);
        setFormData(center);
        setView('detail');
        loadCenterData(centerId);
      }
    }
  }, [searchParams, centers]);

  async function loadCenters() {
    try {
      const res = await authAxios.get(API + '/center/my-centers', { withCredentials: true });
      setCenters(res.data.centers || []);
      
      // If only one center, select it automatically
      if (res.data.centers.length === 1) {
        setSelectedCenter(res.data.centers[0]);
        setFormData(res.data.centers[0]);
        setView('detail');
        loadCenterData(res.data.centers[0].center_id);
      } else if (res.data.centers.length === 0) {
        setView('list');
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function loadCenterData(centerId) {
    try {
      const [messagesRes, verificationRes, analyticsRes, premiumRes, referralsRes] = await Promise.all([
        authAxios.get(API + '/center/messages?center_id=' + centerId, { withCredentials: true }).catch(() => ({ data: { messages: [] } })),
        authAxios.get(API + '/center/' + centerId + '/verification-status', { withCredentials: true }).catch(() => ({ data: null })),
        authAxios.get(API + '/center/analytics', { withCredentials: true }).catch(() => ({ data: null })),
        authAxios.get(API + '/center/' + centerId + '/premium-status', { withCredentials: true }).catch(() => ({ data: null })),
        authAxios.get(API + '/center/referrals', { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setMessages(messagesRes.data.messages || []);
      setVerificationStatus(verificationRes.data);
      setAnalytics(analyticsRes.data);
      setPremiumStatus(premiumRes.data);
      setReferralData(referralsRes.data);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleMarkAdmission() {
    if (!admissionModal || !admissionFee) {
      toast.error('Ingresa la mensualidad del paciente');
      return;
    }
    setSubmittingAdmission(true);
    try {
      const res = await authAxios.put(API + '/center/referral/mark-admission', {
        message_id: admissionModal.message_id,
        monthly_fee: parseInt(admissionFee)
      }, { withCredentials: true });
      toast.success('Ingreso registrado. Comisión: $' + res.data.commission_amount.toLocaleString('es-CL') + ' CLP');
      setAdmissionModal(null);
      setAdmissionFee('');
      loadCenterData(selectedCenter.center_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al registrar ingreso');
    }
    setSubmittingAdmission(false);
  }

  function selectCenter(center) {
    setSelectedCenter(center);
    setFormData(center);
    setView('detail');
    setSearchParams({ id: center.center_id });
    loadCenterData(center.center_id);
  }

  function backToList() {
    setView('list');
    setSelectedCenter(null);
    setSearchParams({});
  }

  async function handleCreateCenter(data) {
    setCreatingCenter(true);
    try {
      const res = await authAxios.post(API + '/center/register', data, { withCredentials: true });
      toast.success('Centro creado exitosamente');
      setShowNewCenterDialog(false);
      loadCenters();
      setSearchParams({ id: res.data.center_id });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear centro');
    }
    setCreatingCenter(false);
  }

  async function handleDeleteCenter(centerId) {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este centro? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.delete(API + '/center/' + centerId, { withCredentials: true });
      toast.success('Centro eliminado');
      loadCenters();
      backToList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  }

  function handleChange(e) {
    setFormData(function(prev) { return Object.assign({}, prev, { [e.target.name]: e.target.value }); });
  }

  function handleMultiSelect(field, value) {
    setFormData(function(prev) {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return Object.assign({}, prev, { [field]: current.filter(function(v) { return v !== value; }) });
      }
      return Object.assign({}, prev, { [field]: current.concat([value]) });
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      var dataToSend = Object.assign({}, formData, {
        price_min: formData.price_min ? parseInt(formData.price_min) : null,
        price_max: formData.price_max ? parseInt(formData.price_max) : null,
        treatment_duration_min: formData.treatment_duration_min ? parseInt(formData.treatment_duration_min) : null,
        treatment_duration_max: formData.treatment_duration_max ? parseInt(formData.treatment_duration_max) : null,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        min_age: formData.min_age ? parseInt(formData.min_age) : null,
        max_age: formData.max_age ? parseInt(formData.max_age) : null,
        // Ensure social media and schedule fields are always sent
        facebook: formData.facebook || '',
        instagram: formData.instagram || '',
        schedule: formData.schedule || '',
        website: formData.website || ''
      });
      await authAxios.put(API + '/center/profile?center_id=' + selectedCenter.center_id, dataToSend, { withCredentials: true });
      toast.success('Centro actualizado');
      loadCenters();
    } catch (err) {
      console.error('Error updating center:', err.response?.data || err);
      var errorDetail = err.response?.data?.detail;
      var errorMsg = 'Error al actualizar';
      if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation error
        errorMsg = errorDetail.map(function(e) { return e.msg || e.message; }).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      }
      toast.error(errorMsg);
    }
    setSaving(false);
  }

  async function handleImageUpload(e, type) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Client-side validation
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('La imagen es muy grande. Máximo 10MB');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(ext || '')) {
      toast.error('Formato no soportado. Use JPG, PNG, WebP, GIF o HEIC');
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await authAxios.post(API + '/upload/image', formDataUpload, { withCredentials: true });
      const imageUrl = res.data.url;
      if (type === 'logo') {
        setFormData(function(prev) { return Object.assign({}, prev, { logo: imageUrl }); });
      } else {
        setFormData(function(prev) {
          const gallery = prev.gallery || [];
          return Object.assign({}, prev, { gallery: gallery.concat([imageUrl]) });
        });
      }
      toast.success('Imagen subida correctamente');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al subir imagen. Intente con otra imagen.';
      toast.error(errorMessage);
      console.error('Upload error:', err);
    }
    setUploading(false);
  }

  function removeGalleryImage(index) {
    setFormData(function(prev) {
      const gallery = (prev.gallery || []).filter(function(_, i) { return i !== index; });
      return Object.assign({}, prev, { gallery: gallery });
    });
  }

  async function requestVerification() {
    setRequestingVerification(true);
    try {
      await authAxios.post(API + '/center/request-verification', {
        document_urls: [],
        notes: verificationNotes
      }, { withCredentials: true });
      toast.success('Solicitud de verificación enviada');
      loadCenterData(selectedCenter.center_id);
      setVerificationNotes('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
    }
    setRequestingVerification(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </div>
    );
  }

  // List View - Show all centers
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-50 mb-2" data-testid="center-dashboard-title">Mis Centros</h1>
                <p className="text-slate-400">Gestiona todos tus centros de rehabilitación</p>
              </div>
              <Button onClick={() => setShowNewCenterDialog(true)} className="bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-2" />Agregar Centro
              </Button>
            </div>

            {centers.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="py-16 text-center">
                  <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-50 mb-2">No tienes centros registrados</h3>
                  <p className="text-slate-400 mb-6">Registra tu primer centro de rehabilitación para comenzar</p>
                  <Button onClick={() => setShowNewCenterDialog(true)} className="bg-emerald-500 hover:bg-emerald-600">
                    <Plus className="w-4 h-4 mr-2" />Registrar mi primer centro
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {centers.map(function(center) {
                  return (
                    <Card key={center.center_id} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer" onClick={() => selectCenter(center)}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          {center.logo ? (
                            <img src={center.logo} alt={center.name} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-20 h-20 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-8 h-8 text-slate-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-slate-50 truncate">{center.name}</h3>
                              {center.is_verified && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{center.city} • {center.address}</p>
                            <div className="flex items-center gap-4">
                              {center.is_public ? (
                                <span className="flex items-center gap-1 text-xs text-emerald-400"><Eye className="w-3 h-3" />Visible</span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-yellow-400">Oculto</span>
                              )}
                              {(center.gallery || []).length > 0 && (
                                <span className="text-xs text-slate-500">{(center.gallery || []).length} fotos</span>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-50" onClick={(e) => { e.stopPropagation(); window.open('/centros/' + center.center_id, '_blank'); }}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Dialog for creating new center */}
            <Dialog open={showNewCenterDialog} onOpenChange={setShowNewCenterDialog}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800">
                <DialogHeader>
                  <DialogTitle className="text-slate-50">Agregar Nuevo Centro</DialogTitle>
                </DialogHeader>
                <CenterForm onSubmit={handleCreateCenter} onCancel={() => setShowNewCenterDialog(false)} loading={creatingCenter} />
              </DialogContent>
            </Dialog>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Detail View - Show selected center
  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          {/* Back button for multiple centers */}
          {centers.length > 1 && (
            <Button variant="ghost" onClick={backToList} className="mb-4 text-slate-400 hover:text-slate-50">
              <ChevronLeft className="w-4 h-4 mr-1" />Volver a mis centros
            </Button>
          )}

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-50 mb-2" data-testid="center-dashboard-title">
                {selectedCenter?.name || 'Panel del Centro'}
              </h1>
              <p className="text-slate-400">Gestiona la información de tu centro</p>
            </div>
            <div className="flex items-center gap-3">
              {selectedCenter?.is_public ? (
                <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm"><Eye className="w-4 h-4" />Visible</span>
              ) : (
                <span className="flex items-center gap-1 px-3 py-1 bg-yellow-500/10 text-yellow-400 rounded-full text-sm">Oculto</span>
              )}
              {selectedCenter?.is_verified && (
                <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm"><CheckCircle className="w-4 h-4" />Verificado</span>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-800 pb-4 overflow-x-auto">
            <Button variant={activeTab === 'info' ? 'default' : 'ghost'} onClick={function() { setActiveTab('info'); }} className={activeTab === 'info' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Building2 className="w-4 h-4 mr-2" />Información</Button>
            <Button variant={activeTab === 'analytics' ? 'default' : 'ghost'} onClick={function() { setActiveTab('analytics'); }} className={activeTab === 'analytics' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><BarChart3 className="w-4 h-4 mr-2" />Estadísticas</Button>
            <Button variant={activeTab === 'gallery' ? 'default' : 'ghost'} onClick={function() { setActiveTab('gallery'); }} className={activeTab === 'gallery' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Image className="w-4 h-4 mr-2" />Galería</Button>
            <Button variant={activeTab === 'premium' ? 'default' : 'ghost'} onClick={function() { setActiveTab('premium'); }} className={activeTab === 'premium' ? 'bg-amber-500' : 'text-amber-400 hover:text-amber-300'}><Crown className="w-4 h-4 mr-2" />Publicidad</Button>
            {false && <Button variant={activeTab === 'referrals' ? 'default' : 'ghost'} onClick={function() { setActiveTab('referrals'); }} className={activeTab === 'referrals' ? 'bg-emerald-500' : 'text-emerald-400 hover:text-emerald-300'}><Users className="w-4 h-4 mr-2" />Referidos</Button>}
            <Button variant={activeTab === 'verification' ? 'default' : 'ghost'} onClick={function() { setActiveTab('verification'); }} className={activeTab === 'verification' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Shield className="w-4 h-4 mr-2" />Verificación</Button>
            <Button variant={activeTab === 'messages' ? 'default' : 'ghost'} onClick={function() { setActiveTab('messages'); }} className={activeTab === 'messages' ? 'bg-emerald-500' : 'text-slate-400 hover:text-slate-50'}><Mail className="w-4 h-4 mr-2" />Mensajes {messages.length > 0 && <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{messages.length}</span>}</Button>
            <Button variant={activeTab === 'quotes' ? 'default' : 'ghost'} onClick={function() { setActiveTab('quotes'); }} className={activeTab === 'quotes' ? 'bg-blue-500' : 'text-blue-400 hover:text-blue-300'} data-testid="quotes-tab"><Inbox className="w-4 h-4 mr-2" />Cotizaciones</Button>
            {selectedCenter && selectedCenter.plan_level >= 3 && (
              <Button variant={activeTab === 'branches' ? 'default' : 'ghost'} onClick={function() { setActiveTab('branches'); }} className={activeTab === 'branches' ? 'bg-purple-500' : 'text-purple-400 hover:text-purple-300'} data-testid="branches-tab"><GitBranch className="w-4 h-4 mr-2" />Sucursales</Button>
            )}
            <div className="flex-1" />
            <Button 
              onClick={function() { setShowCenterEditor(true); }} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Editar Centro
            </Button>
          </div>

          {activeTab === 'analytics' && (
            <ProfileAnalyticsDashboard analytics={analytics} entityType="center" />
          )}

          {activeTab === 'info' && (
            <div className="space-y-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader><CardTitle className="text-slate-50">Información básica</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Nombre</Label>
                    <Input name="name" value={formData.name || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-300">Descripción</Label>
                      <AIDescriptionButton
                        entityType="center"
                        name={formData.name}
                        context={{
                          ciudad: formData.city,
                          servicios: formData.services?.join(', '),
                          tipoTratamiento: formData.treatment_type
                        }}
                        onGenerated={function(desc) { setFormData(function(prev) { return Object.assign({}, prev, { description: desc }); }); }}
                      />
                    </div>
                    <Textarea name="description" value={formData.description || ''} onChange={handleChange} rows={4} className="bg-slate-950 border-slate-700 text-slate-200" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Dirección</Label>
                      <Input name="address" value={formData.address || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Región</Label>
                      <Select value={formData.region || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { region: v }); }); }}>
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar región" /></SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {CHILEAN_REGIONS.map(function(r) { return <SelectItem key={r.value} value={r.value} className="text-slate-300 focus:bg-slate-800">{r.label}</SelectItem>; })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Ciudad</Label>
                      <Select value={formData.city || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { city: v }); }); }}>
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {(formData.region ? getCitiesByRegion(formData.region) : cities).map(function(c) { return <SelectItem key={c} value={c} className="text-slate-300 focus:bg-slate-800">{c}</SelectItem>; })}
                          <SelectItem value="otra" className="text-slate-300 focus:bg-slate-800">Otra ciudad</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Capacidad (pacientes)</Label>
                      <Input name="capacity" type="number" value={formData.capacity || ''} onChange={handleChange} placeholder="Ej: 30" className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Teléfono</Label>
                      <Input name="phone" value={formData.phone || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">WhatsApp</Label>
                      <Input name="whatsapp" value={formData.whatsapp || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Video de YouTube</Label>
                    <Input name="youtube_video" value={formData.youtube_video || ''} onChange={handleChange} placeholder="https://www.youtube.com/watch?v=..." className="bg-slate-950 border-slate-700 text-slate-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader><CardTitle className="text-slate-50">Detalles del tratamiento</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Precio desde (CLP)</Label>
                      <Input name="price_min" type="number" value={formData.price_min || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Precio hasta (CLP)</Label>
                      <Input name="price_max" type="number" value={formData.price_max || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Duración mínima (meses)</Label>
                      <Input name="treatment_duration_min" type="number" value={formData.treatment_duration_min || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Duración máxima (meses)</Label>
                      <Input name="treatment_duration_max" type="number" value={formData.treatment_duration_max || ''} onChange={handleChange} className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Género</Label>
                    <Select value={formData.gender || ''} onValueChange={function(v) { setFormData(function(prev) { return Object.assign({}, prev, { gender: v }); }); }}>
                      <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">{genderOptions.map(function(g) { return <SelectItem key={g.value} value={g.value} className="text-slate-300 focus:bg-slate-800">{g.label}</SelectItem>; })}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-3 block">Modalidad</Label>
                    <div className="flex flex-wrap gap-2">
                      {modalityOptions.map(function(m) {
                        return <Button key={m.value} type="button" variant={(formData.modality || []).includes(m.value) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('modality', m.value); }} className={(formData.modality || []).includes(m.value) ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{m.label}</Button>;
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader><CardTitle className="text-slate-50">Talleres</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {workshopOptions.map(function(w) {
                      return <Button key={w} type="button" variant={(formData.workshops || []).includes(w) ? 'default' : 'outline'} size="sm" onClick={function() { handleMultiSelect('workshops', w); }} className={(formData.workshops || []).includes(w) ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}>{w}</Button>;
                    })}
                  </div>
                  <div>
                    <Label className="text-slate-300">Otros talleres (presiona Enter para agregar)</Label>
                    {formData.custom_workshops && formData.custom_workshops.trim() && (
                      <div className="flex flex-wrap gap-2 my-3">
                        {formData.custom_workshops.split(',').filter(function(t) { return t.trim(); }).map(function(tag, idx) {
                          return (
                            <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 border border-emerald-500 text-emerald-400 rounded-lg text-sm">
                              {tag.trim()}
                              <button
                                type="button"
                                onClick={function() {
                                  var tags = formData.custom_workshops.split(',').filter(function(t) { return t.trim(); });
                                  tags.splice(idx, 1);
                                  setFormData(function(prev) { return Object.assign({}, prev, { custom_workshops: tags.join(', ') }); });
                                }}
                                className="ml-1 hover:text-red-400"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <Input 
                      placeholder="Escribe y presiona Enter..."
                      className="bg-slate-950 border-slate-700 text-slate-200"
                      onKeyDown={function(e) {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          var newTag = e.target.value.trim();
                          if (newTag) {
                            var currentTags = formData.custom_workshops ? formData.custom_workshops.split(',').filter(function(t) { return t.trim(); }) : [];
                            if (!currentTags.includes(newTag)) {
                              currentTags.push(newTag);
                              setFormData(function(prev) { return Object.assign({}, prev, { custom_workshops: currentTags.join(', ') }); });
                            }
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader><CardTitle className="text-slate-50 flex items-center gap-2"><Globe className="w-5 h-5 text-blue-400" />Redes Sociales y Web</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Sitio Web</Label>
                      <Input name="website" value={formData.website || ''} onChange={handleChange} placeholder="https://www.ejemplo.cl" className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <Input name="email" value={formData.email || ''} onChange={handleChange} placeholder="contacto@ejemplo.cl" className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Facebook</Label>
                      <Input name="facebook" value={formData.facebook || ''} onChange={handleChange} placeholder="https://facebook.com/tucentro" className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Instagram</Label>
                      <Input name="instagram" value={formData.instagram || ''} onChange={handleChange} placeholder="https://instagram.com/tucentro" className="bg-slate-950 border-slate-700 text-slate-200" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Horarios de Atención</Label>
                    <Input name="schedule" value={formData.schedule || ''} onChange={handleChange} placeholder="Lunes a Viernes 9:00 - 18:00" className="bg-slate-950 border-slate-700 text-slate-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-50">Mostrar en directorio</p>
                      <p className="text-sm text-slate-400">Visible en búsquedas públicas</p>
                    </div>
                    <Switch checked={formData.is_public || false} onCheckedChange={function(c) { setFormData(function(prev) { return Object.assign({}, prev, { is_public: c }); }); }} />
                  </div>
                </CardContent>
              </Card>

              {/* Vista Previa - Real-time preview of how the center will look in the public directory */}
              <CenterPreview data={formData} />

              <div className="flex gap-4">
                <Button onClick={handleSave} className="flex-1 py-6 bg-emerald-500 hover:bg-emerald-600 rounded-lg" disabled={saving}>
                  {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</> : <><Save className="w-5 h-5 mr-2" />Guardar cambios</>}
                </Button>
                <Button variant="outline" onClick={() => handleDeleteCenter(selectedCenter.center_id)} className="py-6 border-red-500/50 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="space-y-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader><CardTitle className="text-slate-50">Logo del Centro</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    {formData.logo ? (
                      <img src={formData.logo} alt="Logo" className="w-32 h-32 rounded-xl object-contain bg-white p-2 border border-slate-700" />
                    ) : (
                      <div className="w-32 h-32 rounded-xl bg-slate-800 flex items-center justify-center">
                        <Building2 className="w-12 h-12 text-slate-600" />
                      </div>
                    )}
                    <div>
                      <input type="file" accept="image/*" onChange={function(e) { handleImageUpload(e, 'logo'); }} className="hidden" id="logo-upload" />
                      <label htmlFor="logo-upload">
                        <Button type="button" asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 cursor-pointer" disabled={uploading} data-testid="center-dashboard-upload-logo-btn">
                          <span>
                            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                            Subir logo
                          </span>
                        </Button>
                      </label>
                      <p className="text-sm text-slate-500 mt-2">JPG, PNG o WebP. Máx 10MB. Recomendado: 400×400px con fondo transparente o blanco.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-50">Galería de fotos</CardTitle>
                    <div>
                      <input type="file" accept="image/*" onChange={function(e) { handleImageUpload(e, 'gallery'); }} className="hidden" ref={fileInputRef} />
                      <Button variant="outline" onClick={function() { fileInputRef.current?.click(); }} className="border-slate-700 text-slate-300 hover:bg-slate-800" disabled={uploading}>
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Agregar foto
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {(formData.gallery || []).length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-xl">
                      <Image className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No hay fotos en la galería</p>
                      <p className="text-sm text-slate-500 mt-1">Sube fotos de tus instalaciones</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {(formData.gallery || []).map(function(img, index) {
                        return (
                          <div key={index} className="relative group">
                            <img src={img} alt="" className="w-full aspect-square object-cover rounded-xl" />
                            <button onClick={function() { removeGalleryImage(index); }} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Button onClick={handleSave} className="w-full py-6 bg-emerald-500 hover:bg-emerald-600 rounded-lg" disabled={saving}>
                {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</> : <><Save className="w-5 h-5 mr-2" />Guardar cambios</>}
              </Button>
            </div>
          )}

          {/* PREMIUM / PUBLICIDAD TAB */}
          {activeTab === 'premium' && (
            <div className="space-y-6">
              {/* Current Status */}
              {premiumStatus?.is_premium ? (
                <Card className="bg-gradient-to-r from-amber-500/20 to-amber-600/20 border-amber-500/30">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-amber-500/30 flex items-center justify-center">
                        <Crown className="w-7 h-7 text-amber-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-amber-400">Plan Premium Activo</h3>
                        <p className="text-slate-300">Tu centro está destacado en el directorio</p>
                        <p className="text-sm text-slate-400 mt-1">Válido por {premiumStatus.days_remaining} días más</p>
                      </div>
                      <div className="text-right">
                        <Sparkles className="w-8 h-8 text-amber-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                      <Crown className="w-10 h-10 text-amber-400" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-50 mb-2">Elige tu Plan</h2>
                    <p className="text-slate-400">Aumenta tu visibilidad y recibe más pacientes</p>
                  </div>

                  {/* Four Plan Options */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    
                    {/* Plan Gratuito */}
                    <Card className="bg-slate-900 border-slate-700">
                      <CardContent className="p-4">
                        <h3 className="text-lg font-bold text-slate-300 mb-1">Gratuito</h3>
                        <p className="text-slate-500 text-xs mb-3">Presencia básica</p>
                        <div className="mb-3">
                          <span className="text-2xl font-bold text-slate-400">$0</span>
                          <span className="text-slate-500 text-sm"> / mes</span>
                        </div>
                        <ul className="space-y-1.5 mb-4 text-xs">
                          <li className="flex items-center gap-2 text-slate-400"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Perfil en directorio</li>
                          <li className="flex items-center gap-2 text-slate-400"><CheckCircle className="w-3.5 h-3.5 text-slate-600" />Información de contacto</li>
                          <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin posición prioritaria</li>
                          <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin badge destacado</li>
                        </ul>
                        <Button variant="outline" disabled className="w-full border-slate-700 text-slate-500 text-sm">
                          Plan Actual
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Plan Destacado */}
                    <Card className="bg-gradient-to-br from-blue-500/10 via-blue-600/5 to-slate-900 border-blue-500/30">
                      <CardContent className="p-4">
                        <h3 className="text-lg font-bold text-blue-400 mb-1">Destacado</h3>
                        <p className="text-slate-400 text-xs mb-3">Top 11-20 del directorio</p>
                        <div className="mb-3">
                          <span className="text-2xl font-bold text-slate-50">$99.000</span>
                          <span className="text-slate-400 text-sm"> / mes</span>
                        </div>
                        <ul className="space-y-1.5 mb-4 text-xs">
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Badge "Destacado"</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Posición Top 20</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Estadísticas básicas</li>
                          <li className="flex items-center gap-2 text-slate-500"><X className="w-3.5 h-3.5 text-slate-700" />Sin prioridad chatbot</li>
                        </ul>
                        <Button
                          onClick={async function() {
                            setLoadingPremium(true);
                            try {
                              const res = await authAxios.post(API + '/center/premium/checkout', {
                                center_id: selectedCenter.center_id,
                                origin_url: window.location.origin,
                                plan_type: 'destacado'
                              }, { withCredentials: true });
                              if (res.data.url && res.data.token) {
                                var form = document.createElement('form');
                                form.method = 'POST';
                                form.action = res.data.url;
                                var input = document.createElement('input');
                                input.type = 'hidden';
                                input.name = 'token_ws';
                                input.value = res.data.token;
                                form.appendChild(input);
                                document.body.appendChild(form);
                                form.submit();
                              } else if (res.data.checkout_url) {
                                window.location.href = res.data.checkout_url;
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Error al procesar el pago');
                            }
                            setLoadingPremium(false);
                          }}
                          disabled={loadingPremium}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-sm"
                          data-testid="checkout-center-destacado"
                        >
                          {loadingPremium ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                          Elegir Plan
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Plan Premium */}
                    <Card className="bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-slate-900 border-amber-500/30 relative overflow-hidden">
                      <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-bl-lg">POPULAR</div>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-bold text-amber-400 mb-1">Premium</h3>
                        <p className="text-slate-400 text-xs mb-3">Top 4-10 del directorio</p>
                        <div className="mb-3">
                          <span className="text-2xl font-bold text-slate-50">$199.000</span>
                          <span className="text-slate-400 text-sm"> / mes</span>
                        </div>
                        <ul className="space-y-1.5 mb-4 text-xs">
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Badge "Premium"</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Posición Top 10</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Prioridad en TerapeutAI</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-amber-400" />Estadísticas avanzadas</li>
                        </ul>
                        <Button
                          onClick={async function() {
                            setLoadingPremium(true);
                            try {
                              const res = await authAxios.post(API + '/center/premium/checkout', {
                                center_id: selectedCenter.center_id,
                                origin_url: window.location.origin,
                                plan_type: 'premium'
                              }, { withCredentials: true });
                              if (res.data.url && res.data.token) {
                                var form = document.createElement('form');
                                form.method = 'POST';
                                form.action = res.data.url;
                                var input = document.createElement('input');
                                input.type = 'hidden';
                                input.name = 'token_ws';
                                input.value = res.data.token;
                                form.appendChild(input);
                                document.body.appendChild(form);
                                form.submit();
                              } else if (res.data.checkout_url) {
                                window.location.href = res.data.checkout_url;
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Error al procesar el pago');
                            }
                            setLoadingPremium(false);
                          }}
                          disabled={loadingPremium}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-sm"
                          data-testid="checkout-center-premium"
                        >
                          {loadingPremium ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                          Elegir Plan
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Plan Elite */}
                    <Card className="bg-gradient-to-br from-purple-500/10 via-purple-600/5 to-slate-900 border-purple-500/30">
                      <CardContent className="p-4">
                        <h3 className="text-lg font-bold text-purple-400 mb-1">Elite</h3>
                        <p className="text-slate-400 text-xs mb-3">Top 1-3 del directorio</p>
                        <div className="mb-3">
                          <span className="text-2xl font-bold text-slate-50">$350.000</span>
                          <span className="text-slate-400 text-sm"> / mes</span>
                        </div>
                        <ul className="space-y-1.5 mb-4 text-xs">
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-purple-400" />Badge "Elite"</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-purple-400" />Posición Top 3</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-purple-400" />Máxima prioridad chatbot</li>
                          <li className="flex items-center gap-2 text-slate-300"><CheckCircle className="w-3.5 h-3.5 text-purple-400" />Soporte VIP 24/7</li>
                        </ul>
                        <Button
                          onClick={async function() {
                            setLoadingPremium(true);
                            try {
                              const res = await authAxios.post(API + '/center/premium/checkout', {
                                center_id: selectedCenter.center_id,
                                origin_url: window.location.origin,
                                plan_type: 'elite'
                              }, { withCredentials: true });
                              if (res.data.url && res.data.token) {
                                var form = document.createElement('form');
                                form.method = 'POST';
                                form.action = res.data.url;
                                var input = document.createElement('input');
                                input.type = 'hidden';
                                input.name = 'token_ws';
                                input.value = res.data.token;
                                form.appendChild(input);
                                document.body.appendChild(form);
                                form.submit();
                              } else if (res.data.checkout_url) {
                                window.location.href = res.data.checkout_url;
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Error al procesar el pago');
                            }
                            setLoadingPremium(false);
                          }}
                          disabled={loadingPremium}
                          className="w-full bg-purple-500 hover:bg-purple-600 text-sm"
                          data-testid="checkout-center-elite"
                        >
                          {loadingPremium ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                          Elegir Plan
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Plan por Referidos - OCULTO TEMPORALMENTE */}
                  {false && (
                  <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                          <Target className="w-8 h-8 text-emerald-400" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                          <h3 className="text-xl font-bold text-slate-50 mb-1">Plan por Referidos</h3>
                          <p className="text-slate-300 text-sm mb-2">
                            <strong className="text-emerald-400">Sin costo inicial.</strong> Solo pagas el 40% de la primera mensualidad cuando un paciente ingresa a través de nuestra plataforma.
                          </p>
                          <p className="text-xs text-slate-500">Ejemplo: Si tu mensualidad es $800.000, pagarías $320.000 por cada ingreso.</p>
                        </div>
                        <a
                          href="/contacto?asunto=plan-referidos-centro"
                          className="flex-shrink-0"
                        >
                          <Button className="bg-emerald-500 hover:bg-emerald-600">
                            <Phone className="w-4 h-4 mr-2" />
                            Solicitar Plan
                          </Button>
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                  )}
                </div>
              )}

              {/* Benefits - Always visible */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    Beneficios Incluidos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-800/50 rounded-lg flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <Star className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-50">Posición Prioritaria</h4>
                        <p className="text-sm text-slate-400">Tu centro aparece primero en el directorio</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <Crown className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-50">Badge "Centro Destacado"</h4>
                        <p className="text-sm text-slate-400">Distintivo dorado visible en tu perfil</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-50">Destacado en Blog</h4>
                        <p className="text-sm text-slate-400">Apareces en artículos como centro recomendado</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <Video className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-50">Prioridad en TerapeutAI</h4>
                        <p className="text-sm text-slate-400">Nuestro chat de IA te recomienda primero</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <Eye className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-50">Analíticas Avanzadas</h4>
                        <p className="text-sm text-slate-400">Estadísticas detalladas de tu perfil</p>
                      </div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-lg flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-50">Verificación Prioritaria</h4>
                        <p className="text-sm text-slate-400">Proceso de verificación más rápido</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* REFERRALS TAB */}
          {activeTab === 'referrals' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-50">{referralData?.stats?.total_contacts || 0}</p>
                        <p className="text-xs text-slate-400">Consultas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-400">{referralData?.stats?.total_admissions || 0}</p>
                        <p className="text-xs text-slate-400">Ingresados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-400">${(referralData?.stats?.pending_commission || 0).toLocaleString('es-CL')}</p>
                        <p className="text-xs text-slate-400">Comisión pendiente</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-50">{referralData?.stats?.conversion_rate || 0}%</p>
                        <p className="text-xs text-slate-400">Conversión</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Info Banner */}
              <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/20">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-300">
                    <strong className="text-emerald-400">Plan por Efectividad:</strong> Marca como "Ingresado" a los pacientes que llegaron a través de Sin Adicciones.
                    Se genera una comisión del <strong>45%</strong> de la primera mensualidad.
                  </p>
                </CardContent>
              </Card>

              {/* Contacts List */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-slate-50">Consultas recibidas</CardTitle>
                </CardHeader>
                <CardContent>
                  {!referralData?.contacts?.length ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                      <p className="text-slate-400">Aún no tienes consultas</p>
                      <p className="text-sm text-slate-500">Las personas que te contacten aparecerán aquí</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {referralData.contacts.map(function(contact) {
                        const isAdmitted = contact.admission_status === 'admitted';
                        return (
                          <div key={contact.message_id} className={'p-4 rounded-xl border ' + (isAdmitted ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700')}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-slate-50">{contact.name}</span>
                                  {isAdmitted && (
                                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center gap-1">
                                      <UserCheck className="w-3 h-3" />Ingresado
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-400">{contact.email}</p>
                                {contact.phone && <p className="text-sm text-slate-500">{contact.phone}</p>}
                                {contact.message && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{contact.message}</p>}
                                <p className="text-xs text-slate-600 mt-2">{new Date(contact.created_at).toLocaleDateString('es-CL')}</p>
                                {isAdmitted && (
                                  <p className="text-sm text-emerald-400 mt-2">
                                    Comisión: ${(contact.commission_amount || 0).toLocaleString('es-CL')} CLP
                                  </p>
                                )}
                              </div>
                              {!isAdmitted && (
                                <Button
                                  size="sm"
                                  onClick={function() { setAdmissionModal(contact); }}
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                >
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Marcar Ingreso
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Admission Modal */}
          <Dialog open={!!admissionModal} onOpenChange={function() { setAdmissionModal(null); setAdmissionFee(''); }}>
            <DialogContent className="bg-slate-900 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-slate-50">Registrar Ingreso de Paciente</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-slate-300 font-medium">{admissionModal?.name}</p>
                  <p className="text-sm text-slate-400">{admissionModal?.email}</p>
                </div>
                <div>
                  <Label className="text-slate-300">Mensualidad del tratamiento (CLP)</Label>
                  <Input
                    type="number"
                    value={admissionFee}
                    onChange={function(e) { setAdmissionFee(e.target.value); }}
                    placeholder="Ej: 800000"
                    className="bg-slate-950 border-slate-700 text-slate-200 mt-2"
                  />
                  {admissionFee && (
                    <p className="text-sm text-emerald-400 mt-2">
                      Comisión (45%): ${Math.round(parseInt(admissionFee) * 0.45).toLocaleString('es-CL')} CLP
                    </p>
                  )}
                </div>
                <Button
                  onClick={handleMarkAdmission}
                  disabled={submittingAdmission || !admissionFee}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  {submittingAdmission ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
                  Confirmar Ingreso
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Al confirmar, se generará una comisión pendiente de pago a Sin Adicciones
                </p>
              </div>
            </DialogContent>
          </Dialog>

          {/* VERIFICATION TAB - Enhanced */}
          {activeTab === 'verification' && (
            <div className="space-y-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-50">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    Verificación de Calidad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {verificationStatus?.is_verified ? (
                    <div className="p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
                      <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-emerald-400 mb-2">Centro Verificado</h3>
                      <p className="text-slate-400 mb-4">Tu centro ha pasado nuestro proceso de verificación de calidad.</p>
                      <div className="grid grid-cols-3 gap-4 mt-6">
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-400">Tratamiento Digno</p>
                        </div>
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-400">Terapias Certificadas</p>
                        </div>
                        <div className="p-3 bg-slate-800/50 rounded-lg">
                          <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-400">Sin Humillaciones</p>
                        </div>
                      </div>
                    </div>
                  ) : verificationStatus?.verification_status === 'pending' || verificationStatus?.verification_status === 'scheduled' ? (
                    <div className="p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-center">
                      <Clock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-yellow-400 mb-2">
                        {verificationStatus?.verification_status === 'scheduled' ? 'Verificación Programada' : 'Solicitud en Revisión'}
                      </h3>
                      <p className="text-slate-400 mb-4">
                        {verificationStatus?.verification_status === 'scheduled' 
                          ? 'Te contactaremos para coordinar la ' + (verificationStatus?.verification_type === 'videocall' ? 'videollamada' : 'visita presencial')
                          : 'Tu solicitud está siendo procesada por nuestro equipo.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Info about verification */}
                      <div className="p-6 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-xl border border-emerald-500/20">
                        <h3 className="text-lg font-semibold text-slate-50 mb-4">¿Qué verificamos?</h3>
                        <p className="text-slate-400 mb-4">
                          Nuestro proceso de verificación incluye una <strong className="text-emerald-400">videollamada o visita presencial</strong> para asegurar que tu centro:
                        </p>
                        <ul className="space-y-3 text-slate-300">
                          <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span>Ofrece un <strong>tratamiento digno</strong> y respetuoso</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span>Cuenta con <strong>programas de terapia</strong> profesionales</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <span>Garantiza un ambiente <strong>libre de humillaciones</strong></span>
                          </li>
                        </ul>
                      </div>

                      {/* Verification type selection */}
                      <div>
                        <Label className="text-slate-300 mb-3 block">Tipo de verificación</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <button
                            type="button"
                            onClick={function() { setVerificationType('videocall'); }}
                            className={'p-4 rounded-xl border-2 transition-all ' + (verificationType === 'videocall' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600')}
                          >
                            <Video className={'w-8 h-8 mx-auto mb-2 ' + (verificationType === 'videocall' ? 'text-emerald-400' : 'text-slate-400')} />
                            <p className={'font-medium ' + (verificationType === 'videocall' ? 'text-emerald-400' : 'text-slate-300')}>Videollamada</p>
                            <p className="text-xs text-slate-500 mt-1">Verificación virtual</p>
                          </button>
                          <button
                            type="button"
                            onClick={function() { setVerificationType('in_person'); }}
                            className={'p-4 rounded-xl border-2 transition-all ' + (verificationType === 'in_person' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600')}
                          >
                            <MapPin className={'w-8 h-8 mx-auto mb-2 ' + (verificationType === 'in_person' ? 'text-emerald-400' : 'text-slate-400')} />
                            <p className={'font-medium ' + (verificationType === 'in_person' ? 'text-emerald-400' : 'text-slate-300')}>Visita Presencial</p>
                            <p className="text-xs text-slate-500 mt-1">Verificación en terreno</p>
                          </button>
                        </div>
                      </div>

                      {/* Commitment checkboxes */}
                      <div className="space-y-3">
                        <Label className="text-slate-300">Compromisos de tu centro</Label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer">
                            <input
                              type="checkbox"
                              checked={verificationChecks.has_dignified_treatment}
                              onChange={function(e) { setVerificationChecks(function(p) { return Object.assign({}, p, { has_dignified_treatment: e.target.checked }); }); }}
                              className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                            />
                            <span className="text-slate-300">Ofrecemos tratamiento digno y humanizado</span>
                          </label>
                          <label className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer">
                            <input
                              type="checkbox"
                              checked={verificationChecks.has_therapy_programs}
                              onChange={function(e) { setVerificationChecks(function(p) { return Object.assign({}, p, { has_therapy_programs: e.target.checked }); }); }}
                              className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                            />
                            <span className="text-slate-300">Contamos con programas de terapia estructurados</span>
                          </label>
                          <label className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer">
                            <input
                              type="checkbox"
                              checked={verificationChecks.no_humiliation_policy}
                              onChange={function(e) { setVerificationChecks(function(p) { return Object.assign({}, p, { no_humiliation_policy: e.target.checked }); }); }}
                              className="w-5 h-5 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500"
                            />
                            <span className="text-slate-300">Garantizamos un ambiente libre de humillaciones</span>
                          </label>
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <Label className="text-slate-300">Notas adicionales (opcional)</Label>
                        <Textarea
                          value={verificationNotes}
                          onChange={function(e) { setVerificationNotes(e.target.value); }}
                          placeholder="Información adicional sobre tu centro, horarios de disponibilidad..."
                          className="bg-slate-950 border-slate-700 text-slate-200"
                          rows={3}
                        />
                      </div>

                      {/* Submit button */}
                      <Button
                        onClick={async function() {
                          if (!verificationChecks.has_dignified_treatment || !verificationChecks.has_therapy_programs || !verificationChecks.no_humiliation_policy) {
                            toast.error('Debes confirmar todos los compromisos para solicitar verificación');
                            return;
                          }
                          setRequestingVerification(true);
                          try {
                            await authAxios.post(API + '/center/verification/request', {
                              center_id: selectedCenter.center_id,
                              verification_type: verificationType,
                              has_dignified_treatment: verificationChecks.has_dignified_treatment,
                              has_therapy_programs: verificationChecks.has_therapy_programs,
                              no_humiliation_policy: verificationChecks.no_humiliation_policy,
                              notes: verificationNotes
                            }, { withCredentials: true });
                            toast.success('Solicitud de verificación enviada');
                            loadCenterData(selectedCenter.center_id);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Error al enviar solicitud');
                          }
                          setRequestingVerification(false);
                        }}
                        disabled={requestingVerification}
                        className="w-full py-6 bg-emerald-500 hover:bg-emerald-600 rounded-lg"
                      >
                        {requestingVerification ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Enviando...</>
                        ) : (
                          <><Shield className="w-5 h-5 mr-2" />Solicitar Verificación</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'messages' && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="text-slate-50">Mensajes de contacto</CardTitle></CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No hay mensajes</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map(function(msg) {
                      return (
                        <div key={msg.message_id} className="p-4 bg-slate-800/50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-200">{msg.sender_name}</span>
                            <span className="text-sm text-slate-500">{new Date(msg.created_at).toLocaleDateString('es-CL')}</span>
                          </div>
                          <p className="text-slate-400 text-sm mb-2">{msg.sender_email}</p>
                          {msg.sender_phone && <p className="text-slate-400 text-sm mb-2">{msg.sender_phone}</p>}
                          <p className="text-slate-300">{msg.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'branches' && selectedCenter && (
            <BranchManager parentCenter={selectedCenter} />
          )}

          {activeTab === 'quotes' && (
            <QuoteRequestsInbox role="center" />
          )}
        </div>
      </main>
      <Footer />
      
      {/* CenterEditor Modal */}
      {showCenterEditor && selectedCenter && (
        <CenterEditor
          center={selectedCenter}
          mode="user"
          onClose={function() { setShowCenterEditor(false); }}
          onSave={async function(updatedData) {
            try {
              await authAxios.put(API + '/center/' + selectedCenter.center_id, updatedData, { withCredentials: true });
              toast.success('Centro actualizado correctamente');
              setShowCenterEditor(false);
              loadCenters();
            } catch (err) {
              console.error(err);
              toast.error('Error al guardar los cambios');
            }
          }}
        />
      )}
    </div>
  );
}
